import { forwardRef, useImperativeHandle, useState } from "react";
import { z } from "zod";
import { MiDetailFields, MiMenu, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { Typography, useTheme } from "@mui/material";
import { useAccountingContext } from "../../AccountingProvider";
import { useAccountingListActionMenuViewModel } from "./useAccountingListActionMenuViewModel";

export const AccountingListActionMenu = forwardRef((props, ref) => {
  const theme = useTheme();
  const {
    id: orderId,
    isInvoiceEnabled,
    modifiedOn,
    onRefresh,
    menuViewConfig = {
      forceCloseOut: false,
      printInvoices: false,
      orderActivityDetails: true,
      voidOrder: false,
    },
    stateManager,
    order = {},
  } = props;

  const { openDrawer, setSelectedOrder } = useAccountingContext();

  const [isForceCloseoutModalOpen, setIsForceCloseoutModalOpen] =
    useState(false);
  const [isVoidOrderModalOpen, setIsVoidOrderModalOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    order,
  }));
  const { isLoading, voidOrder, forceComplete, getCombinedPdf, handlePrint } =
    useAccountingListActionMenuViewModel({ onRefresh, stateManager });

  const handleForceCloseoutModalOpen = () => {
    setIsForceCloseoutModalOpen(true);
  };

  const handleVoidOrderModalOpen = () => {
    setIsVoidOrderModalOpen(true);
  };
  const edit = useEdit({ forceCompleteReason: "" });

  const forceCompleteFields = [
    {
      label: "Force Closeout Notes",
      name: "forceCompleteReason",
      required: true,
      multiline: true,
      rows: 2,
      validationRule: z.string().min(1, "Reason is required."),
    },
  ];

  const closeModal = () => {
    setIsForceCloseoutModalOpen(false);
    setIsVoidOrderModalOpen(false);
    edit.reset();
  };

  const handleForceComplete = async () => {
    forceComplete({
      orderId,
      modifiedOn,
      ...edit.getValue(),
    });
    closeModal();
  };

  const handlePrintInvoices = async () => {
    const pdf = await getCombinedPdf(orderId);
    if (pdf) {
      handlePrint(pdf.pdfFile);
    }
  };

  const handleVoidOrder = async () => {
    await voidOrder({ orderId, modifiedOn });
    closeModal();
  };

  return (
    <>
      <MiMenu
        data={[
          {
            label: "Force Closeout",
            icon: mdi[ICONS_WEB.CHECK],
            disabled: isInvoiceEnabled,
            color: theme.palette.success.main,
            action: handleForceCloseoutModalOpen,
            hide: !menuViewConfig.forceCloseOut,
          },
          {
            label: "Print Invoice(s)",
            icon: mdi[ICONS_WEB.PRINTER_OUTLINE],
            color: theme.palette.success.main,
            action: handlePrintInvoices,
            hide: !menuViewConfig.printInvoices,
          },
          {
            label: "Void Order",
            icon: mdi[ICONS_WEB.CANCEL],
            color: theme.palette.error.main,
            action: handleVoidOrderModalOpen,
            hide: !menuViewConfig.voidOrder,
          },
          {
            label: "Details",
            icon: mdi.mdiFormatListBulleted,
            color: theme.palette.grey.main,
            action: () => {
              setSelectedOrder(order);
              openDrawer();
            },
            hide: !menuViewConfig.orderActivityDetails,
          },
        ]}
      />
      <MiModal
        open={isForceCloseoutModalOpen}
        closeModal={closeModal}
        width={480}
        titleIcon={mdi[ICONS_WEB.CHECK]}
        leftIconColor={theme.palette.primary.main}
        title="Force Closeout Order"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: closeModal,
          },
          {
            name: "Force Closeout",
            variant: BUTTON_VARIANTS.CONTAINED,
            disabled: !edit.isValid() || !edit.isDirty() || isLoading,
            onClick: handleForceComplete,
          },
        ]}
      >
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          color="grey"
          mb={2}
          px={2}
          fontSize={theme.fontSize.medium}
        >
          Force close out for this order will allow for invoicing to be
          performed
        </Typography>
        <MiDetailFields edit={edit} detailFields={forceCompleteFields} />
      </MiModal>
      <MiModal
        open={false}
        closeModal={closeModal}
        width={400}
        titleIcon={mdi[ICONS_WEB.CALENDAR_START_OUTLINE]}
        leftIconColor={theme.palette.primary.main}
        title="Export to QuickBooks"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: closeModal,
          },
          {
            name: "Submit",
            variant: BUTTON_VARIANTS.CONTAINED,
            disabled: !edit.isValid() || !edit.isDirty(),
          },
        ]}
      />
      <MiModal
        open={isVoidOrderModalOpen}
        closeModal={closeModal}
        width={400}
        titleIcon={mdi[ICONS_WEB.CANCEL]}
        leftIconColor={theme.palette.red.dark}
        title="Void Order"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: closeModal,
          },
          {
            name: "Void Order",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.RED,
            onClick: handleVoidOrder,
          },
        ]}
      >
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          color="grey"
          mb={2}
          px={2}
          fontSize={theme.fontSize.medium}
        >
          Voiding an order will reset any payment information and invoices
          associated with this order. Are you sure you want to void this order?
        </Typography>
      </MiModal>
    </>
  );
});
